import React from 'react';
import { Box, Flex, Text, HStack, Badge, VStack, Tooltip } from '@chakra-ui/react';
import {
  Buildings,
  ArrowCircleDown,
  ArrowCircleUp,
  Package,
  WarningCircle,
  MapPin,
} from 'phosphor-react';
import {
  formatCurrency,
  formatCPFOrCNPJ,
  getDocumentType,
} from '../../../../../utils/FormatFunctions';
import { IEmpresaFiscal } from '../../../../../hooks/useFiscalContabil';
import { COLORS } from '../constants';

interface FiscalContabilCompanyCardProps {
  companyId: string;
  totalRegistros: number;
  totalValor: number;
  totalEntradas: number;
  totalSaidas: number;
  totalServicos: number;
  divergencias?: number;
  empresa?: IEmpresaFiscal;
  onClick: () => void;
}

export const FiscalContabilCompanyCard = React.memo(function FiscalContabilCompanyCard({
  companyId,
  totalRegistros,
  totalValor,
  totalEntradas,
  totalSaidas,
  totalServicos,
  divergencias = 0,
  empresa,
  onClick,
}: FiscalContabilCompanyCardProps) {
  const hasDivergencias = divergencias > 0;

  const getBorderColor = () => {
    if (hasDivergencias) return COLORS.divergencia.border;
    if (totalEntradas > totalSaidas && totalEntradas > totalServicos) return COLORS.entrada.border;
    if (totalSaidas > totalEntradas && totalSaidas > totalServicos) return COLORS.saida.border;
    if (totalServicos > 0) return COLORS.servico.border;
    return 'blue.400';
  };

  const documentInfo = empresa?.cnpj
    ? { type: getDocumentType(empresa.cnpj), formatted: formatCPFOrCNPJ(empresa.cnpj) }
    : null;

  return (
    <Box
      bg="white"
      borderRadius="xl"
      border="1px"
      borderColor={hasDivergencias ? 'red.200' : 'gray.200'}
      borderLeft="4px solid"
      borderLeftColor={getBorderColor()}
      cursor="pointer"
      transition="all 0.2s"
      _hover={{
        borderColor: hasDivergencias ? 'red.300' : 'blue.300',
        boxShadow: 'md',
        transform: 'translateY(-2px)',
      }}
      onClick={onClick}
      p={4}
    >
      <VStack align="stretch" spacing={2} mb={3}>
        <Flex justify="space-between" align="flex-start">
          <HStack spacing={2} flex={1} minW={0}>
            <Badge colorScheme="gray" fontSize="xs" flexShrink={0}>
              {companyId}
            </Badge>
            <Buildings size={18} color="#2B6CB0" style={{ flexShrink: 0 }} />
            <Text
              fontWeight="bold"
              color="gray.700"
              noOfLines={1}
              title={empresa?.razao_social || `Empresa ${companyId}`}
            >
              {empresa?.razao_social || `Empresa ${companyId}`}
            </Text>
            {!empresa && (
              <Tooltip
                label="Dados cadastrais não encontrados para esta empresa"
                placement="top"
                hasArrow
              >
                <Badge colorScheme="yellow" fontSize="9px" flexShrink={0}>
                  dados incompletos
                </Badge>
              </Tooltip>
            )}
          </HStack>
          <Badge colorScheme="blue" fontSize="xs" ml={2} flexShrink={0}>
            {totalRegistros} reg.
          </Badge>
        </Flex>

        <HStack spacing={3} pl={0} flexWrap="wrap">
          {documentInfo && (
            <Text fontSize="xs" color="gray.500">
              {documentInfo.type}: {documentInfo.formatted}
            </Text>
          )}
          {(empresa?.cidade || empresa?.uf) && (
            <HStack spacing={1}>
              <MapPin size={12} color="#718096" />
              <Text fontSize="xs" color="gray.500">
                {[empresa?.cidade, empresa?.uf].filter(Boolean).join(' - ')}
              </Text>
            </HStack>
          )}
          {!empresa && !documentInfo && (
            <Text fontSize="xs" color="gray.400">
              Sem dados cadastrais
            </Text>
          )}
        </HStack>
      </VStack>

      <Flex justify="space-between" align="center" gap={2} flexWrap="wrap">
        <Flex gap={2} flexWrap="wrap">
          {totalEntradas > 0 && (
            <HStack spacing={1} bg="green.50" px={2} py={1} borderRadius="md">
              <ArrowCircleDown size={14} color="#38A169" />
              <Text fontSize="xs" color="green.700" fontWeight="medium">
                {totalEntradas}
              </Text>
            </HStack>
          )}
          {totalSaidas > 0 && (
            <HStack spacing={1} bg="orange.50" px={2} py={1} borderRadius="md">
              <ArrowCircleUp size={14} color="#DD6B20" />
              <Text fontSize="xs" color="orange.700" fontWeight="medium">
                {totalSaidas}
              </Text>
            </HStack>
          )}
          {totalServicos > 0 && (
            <HStack spacing={1} bg="purple.50" px={2} py={1} borderRadius="md">
              <Package size={14} color="#805AD5" />
              <Text fontSize="xs" color="purple.700" fontWeight="medium">
                {totalServicos}
              </Text>
            </HStack>
          )}
          {hasDivergencias && (
            <HStack spacing={1} bg="red.50" px={2} py={1} borderRadius="md">
              <WarningCircle size={14} color="#E53E3E" weight="fill" />
              <Text fontSize="xs" color="red.700" fontWeight="medium">
                {divergencias}
              </Text>
            </HStack>
          )}
        </Flex>

        <Text fontSize="lg" fontWeight="bold" color="gray.800">
          {formatCurrency(totalValor)}
        </Text>
      </Flex>
    </Box>
  );
});
